import moment from 'moment';
import {
    GOAL_CONDITION,
    GOAL_METRIC,
    GOAL_SCOPE,
    GOAL_STATUSES,
    MAX_ENTRIES,
    MAX_ENTRY_VALUE,
    SHELF_MODES,
    TOTAL_BUDGET_ENTRY_LABEL,
} from './constants';

// /goals/list serves display-formatted dates (e.g. "Jun 01, 2026").
const LIST_DATE_FORMAT = 'MMM DD, YYYY';

const CUSTOM_DIMENSION_PREFIX = 'custom-';

// Mirrors the backend GoalHelper.slugify - entry slugs must keep matching
// goal_details.entries[].slug_name for edit-mode value restoration.
export const slugify = name =>
    String(name)
        .toLowerCase()
        .trim()
        .replace(/ - /g, ' ')
        .replace(/\s+/g, '-');

// Group digits with commas (12345 -> "12,345").
export const formatDigits = value => {
    const digits = String(value == null ? '' : value).replace(/[^\d]/g, '');
    return digits.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
};

export const parseDigits = value => String(value == null ? '' : value).replace(/[^\d]/g, '');

// parseDigits capped at MAX_ENTRY_VALUE - sub-goal values persist into a 32-bit column.
export const parseEntryValue = value => {
    const digits = parseDigits(value);
    if (!digits) {
        return '';
    }
    return Number(digits) > MAX_ENTRY_VALUE ? String(MAX_ENTRY_VALUE) : digits;
};

export const formatAmount = value => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '';
    }
    return formatDigits(String(Math.round(Number(value))));
};

// Bar width caps at 100 while the label keeps the true value.
export const getBarWidthPercent = value => {
    const percent = parseFloat(String(value == null ? '' : value).replace('%', ''));
    if (Number.isNaN(percent)) {
        return 0;
    }
    return Math.max(Math.min(percent, 100), 0);
};

export const isValidPercent = value => /^-?\d+(\.\d+)?%$/.test(String(value == null ? '' : value).trim());

// The save payload strips the custom dimension prefix inside entry filters
// (parsed by the backend GoalHelper.get_entry_expression).
export const entryFilterName = filterName =>
    filterName.indexOf(CUSTOM_DIMENSION_PREFIX) === 0 ? filterName.slice(CUSTOM_DIMENSION_PREFIX.length) : filterName;

// Order participating breakdowns exactly like the backend GoalHelper.get_entry_name so a
// generated entry slug matches goal_details.entries[].slug_name on edit: standard filters
// keep their /goals/user_filters order (which already mirrors ordered_filter_names), then
// custom dimensions, ordered by int(uuid[:6], 16) on the "custom-" stripped name. The
// endpoint emits custom dimensions in unsorted DB order, so without this a goal broken down
// by 2+ custom dimensions would hydrate blank sub-goal values (and a re-save could reassign
// budgets between sub-goals).
const customDimensionHexOrder = filterName => parseInt(entryFilterName(filterName).slice(0, 6), 16) || 0;

const orderEntryFilters = filters =>
    filters
        .map((filter, index) => ({ filter, index }))
        .sort((a, b) => {
            const aCustom = a.filter.name.indexOf(CUSTOM_DIMENSION_PREFIX) === 0;
            const bCustom = b.filter.name.indexOf(CUSTOM_DIMENSION_PREFIX) === 0;
            if (aCustom !== bCustom) {
                return aCustom ? 1 : -1;
            }
            if (aCustom) {
                return customDimensionHexOrder(a.filter.name) - customDimensionHexOrder(b.filter.name);
            }
            return a.index - b.index;
        })
        .map(entry => entry.filter);

// Cartesian product of the selected tags across breakdowns that have at least one tag, in
// the backend's canonical entry-name order (see orderEntryFilters). No participating
// breakdown yields the single unfiltered "Total Budget" entry (slug matches the backend
// Goal.NO_FILTER_DEFAULT_NAME slug).
export const buildEntries = (availableFilters, selection) => {
    const participating = orderEntryFilters(
        availableFilters.filter(filter => (selection[filter.name] || []).length > 0)
    );

    if (!participating.length) {
        return {
            entries: [{ key: slugify(TOTAL_BUDGET_ENTRY_LABEL), label: TOTAL_BUDGET_ENTRY_LABEL, filterAttrs: {} }],
            truncated: false,
            isTotalBudgetOnly: true,
        };
    }

    // Only the first MAX_ENTRIES combinations are displayed/saved, so generation stops
    // at MAX_ENTRIES + 1 (enough to detect truncation). Output order is nested-loop
    // order, so capping each intermediate level preserves the first MAX_ENTRIES + 1
    // final combinations while keeping high-cardinality "Select all" picks cheap.
    const generationCap = MAX_ENTRIES + 1;
    let combos = [{ names: [], attrs: {} }];
    for (const filter of participating) {
        const next = [];
        for (const combo of combos) {
            for (const tag of selection[filter.name]) {
                next.push({
                    names: [...combo.names, tag.name],
                    attrs: { ...combo.attrs, [entryFilterName(filter.name)]: tag.id },
                });
                if (next.length === generationCap) {
                    break;
                }
            }
            if (next.length === generationCap) {
                break;
            }
        }
        combos = next;
    }

    const truncated = combos.length > MAX_ENTRIES;
    const entries = combos.slice(0, MAX_ENTRIES).map(combo => {
        const label = combo.names.join(' - ');
        return { key: slugify(label), label, filterAttrs: combo.attrs };
    });

    return { entries, truncated, isTotalBudgetOnly: false };
};

export const computeTotalBudget = (entries, entryValues) =>
    entries.reduce((sum, entry) => sum + (Number(entryValues[entry.key]) || 0), 0);

// Exact POST /goals/api contract (parsed by GoalHelper.edit_goal / get_filter_map).
export const buildSavePayload = ({
    goalId,
    name,
    startDate,
    endDate,
    availableFilters,
    selection,
    entries,
    entryValues,
}) => ({
    id: goalId || null,
    name,
    metric: GOAL_METRIC,
    condition: GOAL_CONDITION,
    scope: GOAL_SCOPE,
    start_date: startDate,
    end_date: endDate,
    filters: availableFilters.reduce((filters, filter) => {
        const selectedTags = selection[filter.name];
        filters[filter.name] = {
            visible: !!selectedTags,
            all_tags: filter.list,
            visible_tags: selectedTags || [],
        };
        return filters;
    }, {}),
    entries: entries.map(entry => ({
        val: entryValues[entry.key] || '',
        filters: entry.filterAttrs,
    })),
});

// goal_details.filters maps filter name -> selected tag ids; an empty list means
// "all tags".
export const hydrateSelection = (availableFilters, goalFilters) => {
    const selection = {};
    Object.keys(goalFilters || {}).forEach(filterName => {
        const filter = availableFilters.find(availableFilter => availableFilter.name === filterName);
        if (!filter) {
            return;
        }

        const selectedIds = goalFilters[filterName];
        selection[filterName] =
            !selectedIds || !selectedIds.length
                ? [...filter.list]
                : filter.list.filter(tag => selectedIds.indexOf(tag.id) !== -1);
    });
    return selection;
};

export const hydrateEntryValues = detailsEntries =>
    (detailsEntries || []).reduce((values, entry) => {
        values[entry.slug_name] = parseDigits(entry.value);
        return values;
    }, {});

export const getGoalStatus = (goal, today = moment()) => {
    // /goals/list serves English month strings (backend strftime "%b %d, %Y"). Parse with an
    // explicit 'en' locale so status and sorting stay correct after the app switches moment's
    // global locale on a language change (Main.jsx moment.locale(...)) - otherwise the month
    // name fails to parse under ja/ko/zh-cn and every goal falls back to ACTIVE.
    const startDate = moment(goal.start_date, LIST_DATE_FORMAT, 'en');
    const endDate = moment(goal.end_date, LIST_DATE_FORMAT, 'en');
    if (!startDate.isValid() || !endDate.isValid()) {
        return GOAL_STATUSES.ACTIVE;
    }
    if (today.isBefore(startDate, 'day')) {
        return GOAL_STATUSES.UPCOMING;
    }
    if (today.isAfter(endDate, 'day')) {
        return GOAL_STATUSES.ENDED;
    }
    return GOAL_STATUSES.ACTIVE;
};

const STATUS_SORT_ORDER = {
    [GOAL_STATUSES.ACTIVE]: 0,
    [GOAL_STATUSES.UPCOMING]: 1,
    [GOAL_STATUSES.ENDED]: 2,
};

// Active goals first, then upcoming, then ended; stable within each group.
export const sortGoalsByStatus = (goals, today = moment()) =>
    [...goals].sort((a, b) => STATUS_SORT_ORDER[getGoalStatus(a, today)] - STATUS_SORT_ORDER[getGoalStatus(b, today)]);

export const filterGoalsByName = (goals, searchText) => {
    const query = (searchText || '').trim().toLowerCase();
    if (!query) {
        return goals;
    }
    return goals.filter(goal => (goal.name || '').toLowerCase().indexOf(query) !== -1);
};

export const parseShelfDeepLink = (hash = window.location.hash) => {
    const query = String(hash || '').split('?')[1];
    if (!query) {
        return null;
    }
    const params = new URLSearchParams(query);
    if (params.get('edit')) {
        return { mode: SHELF_MODES.EDIT, goalId: params.get('edit') };
    }
    if (params.get('duplicate')) {
        return { mode: SHELF_MODES.DUPLICATE, goalId: params.get('duplicate') };
    }
    if (params.get('new')) {
        return { mode: SHELF_MODES.CREATE, goalId: null };
    }
    return null;
};

export const clearShelfDeepLink = () => {
    const [hashPath] = String(window.location.hash || '').split('?');
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hashPath}`);
};

// Keeps the original event names so existing analytics keep working.
export const trackGoalsEvent = (event, params = {}) => {
    try {
        window.mixpanel.track(event, params);
    } catch (e) {
        // mixpanel unavailable (tests, blockers) - tracking is best-effort
    }
};

// Create-mode default duration: today through one month from today.
export const getDefaultGoalSpan = (today = moment()) => ({
    startDate: today.format('YYYY-MM-DD'),
    endDate: today
        .clone()
        .add(1, 'months')
        .format('YYYY-MM-DD'),
});
